import { useState, useEffect } from 'react';
import { DocumentData } from 'firebase/firestore'; // Import DocumentData
import { PMT2ProblemsData } from '../../functions/src/types/pmt2Types'; // Adjust path if needed
import {
    getPmt2ProblemsData,
    getPmt2CaMicroAutoIssues,
    getPmt2AverageInventoryLevels,
    getPmt2ProfitDeclineSkus,
    getPmt2ProductDataForCompany,
    getClientUrlDatabaseItem,
    getOutOfStockStats, // Import the new service function
} from '../services/pmt3ProblemsDashboardService';
import { usePMT3Selection } from '@/context/PMT3SelectionContext'; // Import context hook

interface ProblemsDashboardData {
    problemsData: PMT2ProblemsData | null;
    caMicroAutoIssues: DocumentData[];
    avgInventoryLevels: DocumentData[];
    profitDeclineSkus: DocumentData[];
    productData: DocumentData[]; // For budget issues etc.
    // Add URLs for links
    budgetCheckerUrl: string | null;
    caAutoBidAdjusterUrl: string | null;
    salesProfitHistoryUrl: string | null;
    // Add Out of Stock data
    outOfStockData: DocumentData | null;
}

interface UsePmt3ProblemsDashboardDataHook {
    isLoading: boolean;
    error: Error | null;
    data: ProblemsDashboardData | null;
    refetch: () => void; // Add a refetch function
}

/**
 * Hook to fetch data for the PMT3 Problems Dashboard using useState and useEffect.
 */
export const usePmt3ProblemsDashboardData = (
    companyName: string | null | undefined
): UsePmt3ProblemsDashboardDataHook => {
    const { selectedProduct } = usePMT3Selection(); // Get selected product for ASIN-specific fetches
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    const [data, setData] = useState<ProblemsDashboardData | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState<number>(0); // State to trigger refetch

    const refetch = () => setRefreshTrigger(prev => prev + 1); // Function to trigger effect
    const currentAsin = selectedProduct?.id; // Get current ASIN ID

    useEffect(() => {
        const loadData = async () => {
            // Reset state before fetching
            setData(null);
            setIsLoading(true);
            setError(null);

            if (!companyName) {
                console.log("No company selected, skipping data fetch.");
                setIsLoading(false);
                return;
            }

            try {
                // Fetch company-wide data first
                const [
                    problemsDataResult,
                    caMicroAutoIssuesResult,
                    avgInventoryLevelsResult,
                    profitDeclineSkusResult,
                    productDataResult,
                    budgetCheckerDoc,
                    caAutoBidAdjusterDoc,
                    salesProfitHistoryDoc,
                ] = await Promise.all([
                    getPmt2ProblemsData(companyName),
                    getPmt2CaMicroAutoIssues(companyName), // Company-wide fetch might be inefficient if only checking one ASIN later
                    getPmt2AverageInventoryLevels(companyName), // Company-wide fetch might be inefficient
                    getPmt2ProfitDeclineSkus(companyName), // Company-wide fetch might be inefficient
                    getPmt2ProductDataForCompany(companyName), // Company-wide fetch might be inefficient
                    getClientUrlDatabaseItem(companyName, 'Budget Checker'),
                    getClientUrlDatabaseItem(companyName, 'CA Auto and Micro Auto Bid Adjuster'),
                    getClientUrlDatabaseItem(companyName, 'Sales & Profit History'),
                ]);

                // Fetch ASIN-specific data only if an ASIN is selected
                let outOfStockResult: DocumentData | null = null;
                if (currentAsin) {
                    outOfStockResult = await getOutOfStockStats(companyName, currentAsin);
                } else {
                     console.log("No product selected, skipping OutOfStockStats fetch.");
                }

                setData({
                    problemsData: problemsDataResult,
                    caMicroAutoIssues: caMicroAutoIssuesResult,
                    avgInventoryLevels: avgInventoryLevelsResult,
                    profitDeclineSkus: profitDeclineSkusResult,
                    productData: productDataResult,
                    budgetCheckerUrl: budgetCheckerDoc?.value ?? null,
                    caAutoBidAdjusterUrl: caAutoBidAdjusterDoc?.value ?? null,
                    salesProfitHistoryUrl: salesProfitHistoryDoc?.value ?? null,
                    outOfStockData: outOfStockResult, // Add fetched data
                });

            } catch (err) {
                console.error("Error fetching Problems Dashboard data:", err);
                setError(err instanceof Error ? err : new Error('Failed to fetch dashboard data'));
                setData(null); // Clear data on error
            } finally {
                setIsLoading(false);
            }
        };

        loadData();

    }, [companyName, currentAsin, refreshTrigger]); // Re-run effect if companyName, ASIN, or refreshTrigger changes

    return { isLoading, error, data, refetch };
};